import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Spinner } from "@fluentui/react-components";
import { useTelephony } from "../../context/TelephonyContext";
import {
    telephonyService,
    type DialNumber,
    type DialTarget,
    type CallLogDto,
} from "../../services/telephonyService";
import { I } from "../redesign/kvpIcons";
import {
    initialsOf,
    counterpartyNumber,
    displayNameOf,
    fmtTime,
    normalizeDialNumber,
} from "./telephonyUtils";
import "../redesign/contactRedesign.css";
import { TelefonEinstellungenKnopf } from "./TelefonGeraeteWahl";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * SchnellwahlModal — globales Overlay zum *Starten* eines Anrufs (SP5, Mockups
 * #12/#13/#15). Aus dem Header-Telefon-Icon (`openSchnellwahl`) geöffnet, global
 * unter dem TelephonyProvider gemountet (neben dem CallWidget).
 *
 * Drei Zustände des Such-Inputs:
 *  - Leer  → Abschnitt „ZULETZT" mit den letzten Anrufen (`listCalls`).
 *  - Text/Name → debounced `dial-search` (Name- UND Nummern-Match) → Trefferliste.
 *  - Nummer-artig (≥3 Ziffern) → zusätzlich oben „<Eingabe> · Diese Nummer wählen".
 *
 * Klick auf einen Treffer → `callNumber(nummer, name)` + schließen. Schließen via
 * X / ESC / Backdrop-Klick. Leichter Backdrop (blur-arm) im kvp-rd-Stil.
 */

const RECENT_LIMIT = 8;
const DEBOUNCE_MS = 250;

const numTypeLabel = (n: DialNumber): string => {
    if (n.label) return n.label;
    if (n.type === "mobile") return "Mobil";
    if (n.type === "phone") return "Festnetz";
    return n.type;
};

const CloseIcon = (
    <svg
        width={18}
        height={18}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 5l10 10M15 5L5 15" />
    </svg>
);

interface FlatHit {
    key: string;
    name: string;
    number: string;
    type: string;
    sub: string;
}

const SchnellwahlModal: React.FC = () => {
    const { schnellwahlOpen, closeSchnellwahl, callNumber, sdkStatus, headset, headsetVerbinden } = useTelephony();

    const registered = sdkStatus === "registered";
    const statusHint =
        sdkStatus === "connecting"
            ? "Telefonie verbindet …"
            : sdkStatus === "error"
              ? "Telefonie-Verbindung fehlgeschlagen"
              : "Telefonie nicht verbunden";

    const [query, setQuery] = useState("");
    const [results, setResults] = useState<DialTarget[]>([]);
    const [searching, setSearching] = useState(false);
    const [recent, setRecent] = useState<CallLogDto[]>([]);

    const inputRef = useRef<HTMLInputElement>(null);
    // Monoton steigende Request-ID → veraltete (überholte) Antworten verwerfen.
    const reqIdRef = useRef(0);

    // Beim Öffnen: Eingabe leeren, „Zuletzt" laden, Fokus setzen.
    useEffect(() => {
        if (!schnellwahlOpen) return;
        setQuery("");
        setResults([]);
        setSearching(false);
        telephonyService
            // „Zuletzt" heißt MEINE letzten Anrufe — ohne diese Angabe bekämen
            // Geschäftsführer die des ganzen Hauses zum Zurückrufen angeboten.
            .listCalls({ limit: RECENT_LIMIT, scope: "own" })
            .then((r) => setRecent(r.calls))
            .catch(() => setRecent([]));
        const t = window.setTimeout(() => inputRef.current?.focus(), 30);
        return () => window.clearTimeout(t);
    }, [schnellwahlOpen]);

    // ESC schließt.
    useEffect(() => {
        if (!schnellwahlOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeSchnellwahl();
            }
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [schnellwahlOpen, closeSchnellwahl]);

    // Debounced dial-search bei Texteingabe.
    useEffect(() => {
        if (!schnellwahlOpen) return;
        const term = query.trim();
        if (!term) {
            setResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        const id = ++reqIdRef.current;
        const t = window.setTimeout(() => {
            telephonyService
                .dialSearch(term)
                .then((rows) => {
                    if (reqIdRef.current !== id) return; // veraltet → ignorieren
                    setResults(rows);
                    setSearching(false);
                })
                .catch(() => {
                    if (reqIdRef.current !== id) return;
                    setResults([]);
                    setSearching(false);
                });
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [query, schnellwahlOpen]);

    const digits = useMemo(() => query.replace(/\D/g, ""), [query]);
    const numberLike = digits.length >= 3;
    // Normalisierte Wähl-Nummer der Roh-Eingabe (führendes „+" behalten, sonst
    // nur Ziffern/`*`/`#`) — exakt das, was beim Klick gewählt wird; auch das
    // Label zeigt diese statt der ungefilterten Eingabe.
    const dialNorm = useMemo(() => normalizeDialNumber(query), [query]);

    const hits = useMemo<FlatHit[]>(() => {
        const out: FlatHit[] = [];
        for (const t of results) {
            for (const n of t.numbers) {
                out.push({
                    key: `${t.contactId}:${n.value}`,
                    name: t.displayName,
                    number: n.value,
                    type: n.type,
                    sub: `${n.value} · ${numTypeLabel(n)}`,
                });
            }
        }
        return out;
    }, [results]);

    const pick = useCallback(
        (number: string, name?: string) => {
            if (!registered) return;
            callNumber(number, name); // schließt den Dialog (siehe Context); normalisiert dort
        },
        [callNumber, registered],
    );

    const dialRaw = useCallback(() => {
        if (!registered) return;
        // Genau die normalisierte Nummer wählen, die das Label anzeigt.
        if (dialNorm) callNumber(dialNorm);
    }, [dialNorm, callNumber, registered]);

    const onInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Enter auf einer Nummer-artigen Eingabe → direkt wählen.
            if (e.key === "Enter" && numberLike) {
                e.preventDefault();
                dialRaw();
            }
        },
        [numberLike, dialRaw],
    );

    if (!schnellwahlOpen) return null;

    const term = query.trim();
    const showRecent = term.length === 0;

    return createPortal(
        <div
            className="kvp-rd sw-backdrop"
            role="presentation"
            onPointerDown={closeSchnellwahl}
        >
            <div
                className="sw-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Schnellwahl"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="sw-head">
                    <span className="sw-head-ico" aria-hidden>
                        {I.phone}
                    </span>
                    <span className="sw-head-title">Schnellwahl</span>
                    <button
                        type="button"
                        className="sw-close"
                        onClick={closeSchnellwahl}
                        aria-label="Schließen"
                        title="Schließen"
                    >
                        {CloseIcon}
                    </button>
                </div>

                <div className="sw-search">
                    {I.search}
                    <input
                        ref={inputRef}
                        className="sw-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKeyDown}
                        placeholder="Nummer eingeben oder Kontakt suchen …"
                        inputMode="tel"
                        aria-label="Nummer oder Kontakt"
                        autoComplete="off"
                    />
                </div>

                <div className="sw-body">
                    {!registered && <div className="sw-empty">{statusHint}</div>}
                    {showRecent ? (
                        <RecentSection recent={recent} onPick={pick} disabled={!registered} />
                    ) : (
                        <>
                            {numberLike && (
                                <button
                                    type="button"
                                    className="sw-raw"
                                    onClick={dialRaw}
                                    disabled={!registered}
                                >
                                    <span className="sw-avatar" aria-hidden>
                                        {I.phone}
                                    </span>
                                    <div className="sw-item-main">
                                        <div className="sw-item-name">{dialNorm || term}</div>
                                        <div className="sw-item-sub">Diese Nummer wählen</div>
                                    </div>
                                    <span className="sw-item-call" aria-hidden>
                                        {I.phone}
                                    </span>
                                </button>
                            )}

                            {searching ? (
                                <div className="sw-state">
                                    <Spinner size="tiny" label="Suche …" />
                                </div>
                            ) : hits.length > 0 ? (
                                <div className="sw-section">
                                    <div className="sw-section-title">Kontakte</div>
                                    {hits.map((h) => (
                                        <button
                                            key={h.key}
                                            type="button"
                                            className="sw-item"
                                            onClick={() => pick(h.number, h.name)}
                                            disabled={!registered}
                                        >
                                            <span className="sw-avatar" aria-hidden>
                                                {initialsOf(h.name) ??
                                                    (h.type === "mobile"
                                                        ? I.smartphone
                                                        : I.phone)}
                                            </span>
                                            <div className="sw-item-main">
                                                <div className="sw-item-name" title={h.name}>
                                                    {h.name}
                                                </div>
                                                <div className="sw-item-sub" title={h.sub}>
                                                    {h.sub}
                                                </div>
                                            </div>
                                            <span className="sw-item-call" aria-hidden>
                                                {I.phone}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                !numberLike && (
                                    <div className="sw-empty">
                                        Keine Kontakte gefunden. Gib eine vollständige Nummer
                                        ein, um direkt zu wählen.
                                    </div>
                                )
                            )}
                        </>
                    )}
                </div>

                {/* Headset (07.10.2026): Jabra abheben = annehmen. Die Freigabe
                    verlangt der Browser einmalig per Klick; danach verbindet
                    sich das Headset bei jedem Laden selbst. Daneben die
                    Telefon-Einstellungen mit Mikrofon, Lautsprecher und
                    Klingelton (12.10.2026). */}
                <div className="sw-headset">
                    {headset.unterstuetzt &&
                        headset.aktiviert &&
                        (headset.geraet ? (
                            <span className="sw-headset-ok" title="Abheben nimmt an, Einlegen legt auf, die Stummtaste schaltet stumm.">
                                Headset verbunden: {headset.geraet}
                            </span>
                        ) : (
                            <button type="button" className="sw-headset-btn" onClick={headsetVerbinden}>
                                Headset verbinden
                            </button>
                        ))}
                    <TelefonEinstellungenKnopf />
                    {headset.fehler && <span className="sw-headset-fehler">{headset.fehler}</span>}
                </div>
            </div>
        </div>,
        document.body,
    );
};

// ── „Zuletzt" — letzte Anrufe (leerer Such-Input) ───────────────────────────
const RecentSection: React.FC<{
    recent: CallLogDto[];
    onPick: (number: string, name?: string) => void;
    disabled?: boolean;
}> = ({ recent, onPick, disabled }) => {
    const items = useMemo(
        () =>
            recent
                .map((c) => ({
                    id: c.id,
                    name: displayNameOf(c),
                    number: counterpartyNumber(c),
                    time: fmtTime(c.startedAt),
                    contactName: c.contactDisplayName ?? undefined,
                }))
                .filter((x) => !!x.number) as {
                id: number;
                name: string;
                number: string;
                time: string;
                contactName?: string;
            }[],
        [recent],
    );

    if (items.length === 0) {
        return <div className="sw-empty">Noch keine Anrufe.</div>;
    }
    return (
        <div className="sw-section">
            <div className="sw-section-title">Zuletzt</div>
            {items.map((it) => (
                <button
                    key={it.id}
                    type="button"
                    className="sw-item"
                    onClick={() => onPick(it.number, it.contactName)}
                    disabled={disabled}
                >
                    <span className="sw-avatar" aria-hidden>
                        {initialsOf(it.contactName) ?? I.phone}
                    </span>
                    <div className="sw-item-main">
                        <div className="sw-item-name" title={it.name}>
                            {it.name}
                        </div>
                        <div className="sw-item-sub">
                            {it.contactName ? `${it.number} · ` : ""}
                            {it.time}
                        </div>
                    </div>
                    <span className="sw-item-call" aria-hidden>
                        {I.phone}
                    </span>
                </button>
            ))}
        </div>
    );
};

export default SchnellwahlModal;
